import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { AlertTriangle, CheckCircle2, Loader2, Mic, MicOff, RotateCw, Volume2 } from "lucide-react";
import { AlertDialog, AlertDialogContent, AlertDialogDescription, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { requestMic, micStillLive, micPermission, setMicStatus } from "@/lib/micGate";
import { MicHelpButton } from "@/components/MicHelpButton";
import { speak, cancelSpeech, unlockAudio } from "@/lib/speech";
import { BlockedHelp } from "@/components/MicBlockedHelp";
import { warmAudio } from "@/lib/recorder";

const Ctx = createContext({ ensure: async () => true, ready: true });
export const useHardwareGate = () => useContext(Ctx);
export const SPEAKER_OK_KEY = "lexv_speaker_ok_at";

const MicStep = ({ state, busy, onEnable }) => {
  const missing = state.kind === "nodevice", unsupported = state.kind === "unsupported";
  return (
    <div className="space-y-4" data-testid="mic-required-panel">
      <div className="flex items-start gap-3">
        <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-rose-100 text-rose-600"><MicOff className="h-5 w-5" /></span>
        <div>
          <AlertDialogTitle data-testid="mic-required-title" className="font-display text-xl">{missing ? "No Microphone Detected" : "Microphone Access Required"}</AlertDialogTitle>
          <AlertDialogDescription data-testid="mic-required-message" className="mt-1 text-slate-600">
            {unsupported ? "This browser can't use a microphone on this page. Please open lexversant.ca in an up-to-date Chrome, Safari, Edge or Firefox."
              : missing ? "We couldn't find a working microphone. Plug in or switch on your headset or microphone, then try again."
                : "This assessment requires audio input to capture and grade your speech. You cannot proceed without enabling your microphone."}
          </AlertDialogDescription>
        </div>
      </div>
      {state.blocked && <BlockedHelp />}
      {!unsupported && (
        <div className="flex flex-wrap gap-2">
          <button data-testid="mic-enable-btn" onClick={onEnable} disabled={busy} className="inline-flex items-center gap-2 rounded-full bg-indigo-600 px-6 py-2.5 text-sm font-bold text-white transition-colors hover:bg-indigo-700 disabled:opacity-60">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mic className="h-4 w-4" />}{state.blocked ? "I've enabled it — check again" : "Enable Microphone"}
          </button>
          {state.blocked && <button data-testid="mic-reload-btn" onClick={() => window.location.reload()} className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"><RotateCw className="h-4 w-4" />Reload page</button>}
        </div>
      )}
      <MicHelpButton state={state} />
      {state.attempts > 0 && !state.blocked && <p data-testid="mic-attempt-note" className="text-xs text-rose-600">Microphone still not enabled (attempt {state.attempts}). When your browser asks, choose Allow.</p>}
    </div>
  );
};

const SpeakerStep = ({ onConfirm }) => {
  const [played, setPlayed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [notHeard, setNotHeard] = useState(false);
  const play = async () => {
    unlockAudio(true); warmAudio(); setBusy(true); setErr(""); setNotHeard(false);
    const r = await speak("This is your LexVersant speaker check. If you can hear this clearly, press I heard it.");
    setBusy(false);
    if (r.ok) setPlayed(true); else setErr("No sound could be played. Turn your volume up, switch off silent mode on a phone, then play it again.");
  };
  return (
    <div className="space-y-4" data-testid="speaker-check-panel">
      <div className="flex items-start gap-3">
        <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-indigo-100 text-indigo-600"><Volume2 className="h-5 w-5" /></span>
        <div>
          <AlertDialogTitle className="font-display text-xl">Speaker Check</AlertDialogTitle>
          <AlertDialogDescription className="mt-1 text-slate-600">
            <span data-testid="mic-ok-note" className="mb-1 flex items-center gap-1.5 font-semibold text-emerald-600"><CheckCircle2 className="h-4 w-4" />Microphone connected</span>
            Play the test sound and confirm you can hear it through your speakers or headset.
          </AlertDialogDescription>
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        <button data-testid="speaker-play-btn" onClick={play} disabled={busy} className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-5 py-2.5 text-sm font-bold text-white transition-colors hover:bg-slate-700 disabled:opacity-60">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Volume2 className="h-4 w-4" />}{played ? "Play again" : "Play test sound"}</button>
        <button data-testid="speaker-heard-btn" onClick={onConfirm} disabled={!played || busy} className="rounded-full bg-indigo-600 px-5 py-2.5 text-sm font-bold text-white transition-colors hover:bg-indigo-700 disabled:opacity-40">I heard it</button>
        {played && <button data-testid="speaker-not-heard-btn" onClick={() => setNotHeard(true)} className="rounded-full px-4 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-100">I didn't hear it</button>}
      </div>
      {err && <p data-testid="speaker-error" className="flex items-start gap-1.5 text-xs text-rose-600"><AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />{err}</p>}
      {notHeard && <p data-testid="speaker-not-heard-tips" className="rounded-lg bg-amber-50 p-3 text-xs text-amber-800">Check that your volume is up and not muted, your headset is plugged in or connected by Bluetooth, and a phone isn't in silent mode. Then play the sound again.</p>}
    </div>
  );
};

// Blocks the session until a live microphone stream exists and the student confirms they hear audio
export const HardwareGate = ({ children }) => {
  const [phase, setPhase] = useState("checking");
  const [mic, setMic] = useState({ kind: "", blocked: false, attempts: 0 });
  const [busy, setBusy] = useState(false);
  const passed = useRef(false);

  const fail = useCallback((r, bump) => { setMic((m) => ({ kind: r.kind, blocked: !!r.blocked, attempts: bump ? m.attempts + 1 : 0 })); setPhase("mic"); setMicStatus("lost"); }, []);
  const watch = useCallback((stream) => {
    stream.getAudioTracks().forEach((t) => t.addEventListener("ended", async () => fail({ kind: "denied", blocked: (await micPermission()) === "denied" }, false), { once: true }));
  }, [fail]);
  const check = useCallback(async (bump) => {
    setBusy(true);
    const r = await requestMic();
    setBusy(false);
    if (!r.ok) return fail(r, bump);
    watch(r.stream);
    if (passed.current) { setMicStatus("ready"); return setPhase("ready"); }
    const fresh = Date.now() - Number(sessionStorage.getItem(SPEAKER_OK_KEY) || 0) < 180000;
    sessionStorage.removeItem(SPEAKER_OK_KEY);
    if (fresh) { passed.current = true; setMicStatus("ready"); setPhase("ready"); } else setPhase("speaker");
  }, [fail, watch]);

  useEffect(() => { check(false); return () => cancelSpeech(); }, [check]);
  // Re-check automatically when the student changes the permission in browser settings
  useEffect(() => {
    let st;
    navigator.permissions?.query({ name: "microphone" }).then((s) => { st = s; s.onchange = () => { if (s.state === "granted") check(false); else if (s.state === "denied") fail({ kind: "denied", blocked: true }, false); }; }).catch(() => {});
    return () => { if (st) st.onchange = null; };
  }, [check, fail]);

  const ensure = useCallback(async () => {
    if (await micStillLive()) return true;
    const r = await requestMic();
    if (r.ok) { watch(r.stream); return true; }
    fail({ ...r, blocked: r.blocked || (await micPermission()) === "denied" }, false);
    return false;
  }, [fail, watch]);

  const ready = phase === "ready";
  return (
    <Ctx.Provider value={{ ensure, ready }}>
      {passed.current || ready ? children : (
        <div data-testid="hardware-gate-placeholder" className="mx-auto grid min-h-[50vh] max-w-xl place-items-center px-4 text-center text-sm text-slate-400">
          <span className="inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />Checking your microphone and speakers…</span>
        </div>
      )}
      <AlertDialog open={!ready} onOpenChange={() => {}}>
        <AlertDialogContent data-testid="hardware-gate-modal" onEscapeKeyDown={(e) => e.preventDefault()} className="max-h-[92vh] w-[calc(100%-2rem)] max-w-lg overflow-y-auto rounded-2xl bg-white">
          {phase === "checking" ? (
            <div className="flex items-center gap-3" data-testid="hardware-gate-checking">
              <Loader2 className="h-5 w-5 animate-spin text-indigo-600" />
              <div><AlertDialogTitle className="text-base">Checking your microphone…</AlertDialogTitle><AlertDialogDescription>If your browser asks for microphone access, choose Allow.</AlertDialogDescription></div>
            </div>
          ) : phase === "mic" ? <MicStep state={mic} busy={busy} onEnable={() => { warmAudio(); check(true); }} />
            : <SpeakerStep onConfirm={() => { warmAudio(); passed.current = true; setMicStatus("ready"); setPhase("ready"); }} />}
        </AlertDialogContent>
      </AlertDialog>
    </Ctx.Provider>
  );
};
